import React from "react";
import { useHistory } from "react-router-dom";
import { Formik, Field } from "formik";
import { Form, message } from "antd";
import { Templates, Fragments, Fields } from "components";
import { schemas, constants } from "common";
import { useSuperAdminCheck, usePermission, useResource } from "hooks";
import { Button } from "components/elements";
import { mapAxiosError } from "common/utils";
import { useUserContext } from "components/providers/User";
import { useAPIContext } from "components/providers/Api";

import css from "./ghostKitchens.module.css";

const { ListItems } = Templates;
const {
  Tables: { InteractiveTable },
  Address: { OneLineAddress },
  AvailabilityStatus,
  Drawers: { ResDrawer },
  Permissions: { Permission },
} = Fragments;
const {
  states,
  permissions: { routes, methods },
} = constants;

const { POST, GET } = methods;
const { LOCATIONS_LIST, GHOST_KITCHEN_LIST } = routes;

const defaultQuery = {
  filters: {
    name: "",
    // city: '',
    // state: '',
    // isActive: true,
  },
  skip: 0,
  limit: 20,
};

const FILTERS = [
  {
    label: "Name",
    fieldName: "filters.name",
    placeholder: "Name",
    input: "Input",
  },
  {
    label: "State",
    fieldName: "filters.state",
    options: states,
    input: "Select",
  },
];

const createKitchen = async (
  formValues,
  actions,
  drawerProps,
  client,
  { Marketplaces, GhostKitchens },
) => {
  const data = formValues;
  const marketplaces = await Marketplaces.readMany();
  const marketplace = marketplaces?.data?.results?.find(
    (item) => item.appId === client,
  );
  data.marketPlace = marketplace.id;
  try {
    await GhostKitchens.create(data);
    message.success("Kitchen Created");
    drawerProps.close();
  } catch (e) {
    message.error(mapAxiosError(e));
  } finally {
    actions.setSubmitting(false);
  }
};

const GhostLocations = ({ ghostLocationId }) => {
  const history = useHistory();
  const { Marketplaces, GhostKitchens } = useAPIContext();
  const { client } = useUserContext();
  const { resource, fetching } = useResource({
    path: "/ghost-kitchens/fetch-eligible-locations",
  });
  const { resource: resourceRG } = useResource({
    path: "/restaurant-groups",
    data: { filters: { name: "" }, limit: 1000 },
  });
  const hasKioskStatusPermission = usePermission({
    requiredPermissions: [
      { method: GET, url: LOCATIONS_LIST, field: "isKioskEnabled" },
    ],
  });
  const isSuperAdmin = useSuperAdminCheck();

  const restaurantGroups = resourceRG.results || [];

  const filters = [...FILTERS];
  if (isSuperAdmin) {
    filters.push({
      label: "Active Locations",
      fieldName: "filters.isActive",
      options: [
        { name: "Show Only Active", value: true },
        { name: "Show All", value: "" },
      ],
      input: "Select",
    });
  }
  return (
    <ListItems
      locationId={ghostLocationId}
      history={history}
      name="Ghost Kitchens"
      searchPath="/ghost-kitchens/"
      defaultQuery={defaultQuery}
      filters={filters}
      filterHeaderButton={() => (
        <Permission
          requiredPermissions={{ method: POST, url: GHOST_KITCHEN_LIST }}
          yes={() => (
            <div className={css.createLocationWrapper}>
              <ResDrawer
                title="Create Kitchen"
                placement="right"
                width="35vw"
                button={<Button size="medium">Create Kitchen</Button>}
              >
                {(drawerProps) => (
                  <Formik
                    initialValues={{
                      appId: "",
                      externalId: "",
                      name: "",
                      restaurantGroups: [],
                      source: "",
                    }}
                    validationSchema={
                      schemas.GhostKitchenSchema.newGhostKitchen
                    }
                    onSubmit={(formValues, actions) =>
                      createKitchen(formValues, actions, drawerProps, client, {
                        Marketplaces,
                        GhostKitchens,
                      })
                    }
                  >
                    {({ handleSubmit, isSubmitting }) => (
                      <Form onSubmit={handleSubmit}>
                        <Field
                          label="Name"
                          name="name"
                          placeholder="Name"
                          component={Fields.Input}
                        />
                        <Field
                          label="External ID"
                          name="externalId"
                          placeholder="External ID"
                          component={Fields.Input}
                        />
                        <Field
                          label="App Id"
                          name="appId"
                          placeholder="App Id"
                          component={Fields.Input}
                        />
                        <Field
                          label="Restaurant Groups"
                          name="restaurantGroups"
                          placeholder="Select Restaurant Groups"
                          mode="multiple"
                          options={restaurantGroups.map((rg) => ({
                            name: rg.name,
                            value: rg.id,
                          }))}
                          filterOption={(input, option) => {
                            if (typeof option.props.children === "string") {
                              return (
                                option.props.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              );
                            }
                            return false;
                          }}
                          component={Fields.Select}
                        />
                        <Field
                          label="Source"
                          name="source"
                          placholder="Source"
                          options={resource.map((location) => ({
                            name: location.name,
                            value: location.id,
                          }))}
                          component={Fields.Select}
                        />
                        <Button
                          htmlType="submit"
                          size="small"
                          color="blue"
                          disabled={isSubmitting || fetching}
                          loading={isSubmitting || fetching}
                        >
                          Create
                        </Button>
                      </Form>
                    )}
                  </Formik>
                )}
              </ResDrawer>
            </div>
          )}
        />
      )}
      table={({
        paginationObj,
        results,
        onPageChange,
        total,
        query,
        fetching,
      }) => (
        <InteractiveTable
          horizontalScrollSize="large"
          dataSource={results}
          pagination={paginationObj(query.skip, query.limit, total)}
          onChange={onPageChange}
          loading={fetching}
          history={history}
          path="/kitchen/"
          columns={[
            {
              title: "Location Name",
              dataIndex: "name",
            },
            {
              title: "Address",
              dataIndex: "address",
              render: (text) => <OneLineAddress {...text} />,
            },
            {
              title: "Location Status",
              dataIndex: "isOpen",
              render: (text) => <AvailabilityStatus available={text} />,
            },
            {
              title: "Delivery Status",
              dataIndex: "isDeliveryEnabled",
              render: (text) => <AvailabilityStatus available={text} />,
            },
            {
              title: "Pickup Status",
              dataIndex: "isPickupEnabled",
              render: (text) => <AvailabilityStatus available={text} />,
            },
            {
              hidden: !hasKioskStatusPermission,
              title: "Kiosk Status",
              dataIndex: "isKioskEnabled",
              render: (text) => <AvailabilityStatus available={text} />,
            },
          ]}
        />
      )}
    />
  );
};

export default GhostLocations;
